import { type EventPayload } from '@side/analytics';

export enum AnalyticsEventName {
  LogoutRedirect = 'logout_redirect',
  SSOPlatformLogin = 'sso_platform_login',
  SSOOktaLogin = 'sso_okta_login',
  GoogleLogin = 'google_login',
  SSOLoginClick = 'sso_login_click',
  GoogleLoginClick = 'google_login_click',
  TeamNotFound = 'team_not_found',
  TeamMemberNotFound = 'team_member_not_found',
  ExternalServiceAddClick = 'external_service_add_click',
  ExternalServiceSaveClick = 'external_service_save_click',
  ExternalServiceRemoveClick = 'external_service_remove_click',
  ExternalServiceAddUserClick = 'external_service_add_user_click',
  TeamDetailTabMount = 'team_detail_tab_mount',
  TeamDetailTabUnmount = 'team_detail_tab_unmount',
  AuthorizeNotificationsDismissed = 'dismiss_authorize_notifications',
  ClickAuthorizeNotifications = 'authorize_notifications',
  // NOTE: Event names for notifications are all the same since they are used as
  // table name in data warehouse - for more information talk to data-eng
  ManageNotifications = 'MANAGE_NOTIFICATIONS',
  AppRefreshModal = 'APP_REFRESH_MODAL',
}

export enum AnalyticsEvent {
  OktaLoginSuccess,
  OktaLoginFailure,
  GoogleLoginFailure,
  OktaLoginClick,
  OktaAutomaticLogin,
  GoogleLoginClick,
  OktaLogoutSuccess,
  OktaLogoutFailure,
  OktaFullLogout,
  TeamNotFound,
  TeamMemberNotFound,
  ExternalServiceAddClick,
  ExternalServiceSaveClick,
  ExternalServiceRemoveClick,
  ExternalServiceAddUserClick,
  TeamDetailTabMount,
  TeamDetailTabUnmount,
  ManageNotifications,
  AppRefreshModalMount,
  AppRefreshModalSnoozeClick,
  AppRefreshModalRefreshClick,
  AuthorizeNotificationsDismissed,
  ClickAuthorizeNotifications,
}

type EventNames = {
  [key in AnalyticsEvent]: AnalyticsEventName;
};

type EventTypePayloads = {
  [key in AnalyticsEvent]: EventPayload & Record<string, unknown>;
};

export const AnalyticsEventNames: EventNames = {
  [AnalyticsEvent.OktaFullLogout]: AnalyticsEventName.LogoutRedirect,
  [AnalyticsEvent.OktaLogoutSuccess]: AnalyticsEventName.LogoutRedirect,
  [AnalyticsEvent.OktaLogoutFailure]: AnalyticsEventName.LogoutRedirect,
  [AnalyticsEvent.OktaLoginSuccess]: AnalyticsEventName.SSOPlatformLogin,
  [AnalyticsEvent.OktaLoginFailure]: AnalyticsEventName.SSOPlatformLogin,
  [AnalyticsEvent.GoogleLoginFailure]: AnalyticsEventName.GoogleLogin,
  [AnalyticsEvent.OktaLoginClick]: AnalyticsEventName.SSOLoginClick,
  [AnalyticsEvent.GoogleLoginClick]: AnalyticsEventName.GoogleLoginClick,
  [AnalyticsEvent.OktaAutomaticLogin]: AnalyticsEventName.SSOOktaLogin,
  [AnalyticsEvent.TeamNotFound]: AnalyticsEventName.TeamNotFound,
  [AnalyticsEvent.TeamMemberNotFound]: AnalyticsEventName.TeamMemberNotFound,
  [AnalyticsEvent.ExternalServiceAddClick]:
    AnalyticsEventName.ExternalServiceAddClick,
  [AnalyticsEvent.ExternalServiceSaveClick]:
    AnalyticsEventName.ExternalServiceSaveClick,
  [AnalyticsEvent.ExternalServiceRemoveClick]:
    AnalyticsEventName.ExternalServiceRemoveClick,
  [AnalyticsEvent.ExternalServiceAddUserClick]:
    AnalyticsEventName.ExternalServiceRemoveClick,
  [AnalyticsEvent.TeamDetailTabMount]: AnalyticsEventName.TeamDetailTabMount,
  [AnalyticsEvent.TeamDetailTabUnmount]:
    AnalyticsEventName.TeamDetailTabUnmount,
  [AnalyticsEvent.ManageNotifications]: AnalyticsEventName.ManageNotifications,
  [AnalyticsEvent.AppRefreshModalMount]: AnalyticsEventName.AppRefreshModal,
  [AnalyticsEvent.AppRefreshModalSnoozeClick]:
    AnalyticsEventName.AppRefreshModal,
  [AnalyticsEvent.AppRefreshModalRefreshClick]:
    AnalyticsEventName.AppRefreshModal,
  [AnalyticsEvent.AuthorizeNotificationsDismissed]:
    AnalyticsEventName.AuthorizeNotificationsDismissed,
  [AnalyticsEvent.ClickAuthorizeNotifications]:
    AnalyticsEventName.ClickAuthorizeNotifications,
};

export const AnalyticsEventPayloads: EventTypePayloads = {
  [AnalyticsEvent.OktaLoginSuccess]: {
    eventContainer: 'sso_platform_identity',
    eventTarget: 'list_view',
    eventType: 'load',
    task: 'redirect',
    redirect: true,
  },
  [AnalyticsEvent.OktaLoginFailure]: {
    eventContainer: 'sso_platform_identity',
    eventTarget: 'list_view',
    eventType: 'load',
    task: 'redirect',
    redirect: false,
  },
  [AnalyticsEvent.OktaLogoutSuccess]: {
    eventContainer: 'logout_identity',
    eventTarget: 'logout_view',
    eventType: 'load',
    task: 'redirect',
    redirect: true,
  },
  [AnalyticsEvent.OktaFullLogout]: {
    eventContainer: 'logout_identity',
    eventTarget: 'logout_view',
    eventType: 'load',
    task: 'redirect',
    redirect: true,
    multi_user: true,
  },
  [AnalyticsEvent.OktaLogoutFailure]: {
    eventContainer: 'logout_identity',
    eventTarget: 'logout_view',
    eventType: 'load',
    task: 'redirect',
    redirect: true,
  },
  [AnalyticsEvent.GoogleLoginFailure]: {
    eventContainer: 'google_identity',
    eventTarget: 'list_view',
    eventType: 'load',
    task: 'redirect',
    redirect: false,
  },
  [AnalyticsEvent.OktaLoginClick]: {
    eventContainer: 'sso_login_modal',
    eventTarget: 'sign_in_button',
    eventType: 'click',
    task: 'login',
  },
  [AnalyticsEvent.GoogleLoginClick]: {
    eventContainer: 'google_login_modal',
    eventTarget: 'sign_in_button',
    eventType: 'click',
    task: 'login',
  },
  [AnalyticsEvent.OktaAutomaticLogin]: {
    eventContainer: 'sso_okta_identity',
    eventTarget: 'list_view',
    eventType: 'load',
    task: 'redirect',
  },
  [AnalyticsEvent.TeamNotFound]: {
    eventContainer: 'team_detail_identity',
    eventTarget: 'team_not_found',
    eventType: 'load',
    task: 'redirect',
    redirect: false,
  },
  [AnalyticsEvent.TeamMemberNotFound]: {
    eventContainer: 'team_member_detail_identity',
    eventTarget: 'team_member_not_found',
    eventType: 'load',
    task: 'redirect',
    redirect: false,
  },
  [AnalyticsEvent.ExternalServiceAddClick]: {
    eventContainer: 'add_external_service_modal',
    eventTarget: 'add_service_button',
    eventType: 'click',
    redirect: false,
    task: 'add',
  },
  [AnalyticsEvent.ExternalServiceSaveClick]: {
    eventContainer: 'external_service_detail',
    eventTarget: 'save_service_button',
    eventType: 'click',
    redirect: false,
    task: 'update',
  },
  [AnalyticsEvent.ExternalServiceRemoveClick]: {
    eventContainer: 'remove_external_service_modal',
    eventTarget: 'remove_button',
    eventType: 'click',
    task: 'remove',
    redirect: false,
  },
  [AnalyticsEvent.ExternalServiceAddUserClick]: {
    eventContainer: 'add_external_service_user_modal',
    eventTarget: 'add_user_button',
    eventType: 'click',
    task: 'add',
    redirect: false,
  },
  [AnalyticsEvent.TeamDetailTabMount]: {
    eventContainer: 'team_detail_identity',
    eventTarget: 'team_view',
    eventType: 'load',
    task: 'view',
  },
  [AnalyticsEvent.TeamDetailTabUnmount]: {
    eventContainer: 'team_detail_identity',
    eventTarget: 'team_view',
    eventType: 'load',
    task: 'view',
  },
  [AnalyticsEvent.ManageNotifications]: {
    eventContainer: 'notifications_preference',
    eventTarget: 'notifications_preference',
    eventType: 'click',
    task: 'view',
  },
  [AnalyticsEvent.AppRefreshModalMount]: {
    eventContainer: 'app_version_refresh_modal',
    eventTarget: 'app_version_refresh_modal',
    eventType: 'load',
    task: 'view',
  },
  [AnalyticsEvent.AppRefreshModalSnoozeClick]: {
    eventContainer: 'app_version_refresh_modal',
    eventTarget: 'snooze_button',
    eventType: 'click',
    task: 'select',
  },
  [AnalyticsEvent.AppRefreshModalRefreshClick]: {
    eventContainer: 'app_version_refresh_modal',
    eventTarget: 'refresh_button',
    eventType: 'click',
    task: 'select',
  },
  [AnalyticsEvent.AuthorizeNotificationsDismissed]: {
    eventContainer: 'notifications_allow_modal',
    eventTarget: 'notifications_allow_modal',
    eventType: 'click',
    task: 'exit',
  },
  [AnalyticsEvent.ClickAuthorizeNotifications]: {
    eventContainer: 'notifications_allow_modal',
    eventTarget: 'allow_notifications_button',
    eventType: 'click',
    task: 'allow_notifications',
  },
};
